import i18n from 'i18next'

import { READ_TOOLS, WRITE_TOOLS } from './registry'
import { AI_CHATBOT_CONFIG } from '../engine/config'
import { getAiDebugLevel } from '../engine/debugStorage'
import { getEntityRegistry, isEntityEnabled } from '../engine/entity-registry'
import { getHostToolPack } from '../engine/host-runtime'
import { getSystemPrompt } from '../engine/prompts'
import type { AiChatbotConfig, PipelineConfig } from '../engine/types'

const PIPELINE: PipelineConfig = {
  guard: { model: AI_CHATBOT_CONFIG.models.guard },
  gather: { model: AI_CHATBOT_CONFIG.models.gather },
  synthesize: { model: AI_CHATBOT_CONFIG.models.synthesize },
}

function resolveDebugLevel(): 'off' | 'console' | 'verbose' {
  return (
    getAiDebugLevel() ??
    (process.env.NODE_ENV === 'development' ? 'verbose' : 'off')
  )
}

function resolveLanguageName(): string {
  const lang = i18n.resolvedLanguage ?? 'en'
  return (
    new Intl.DisplayNames(['en'], { type: 'language' }).of(lang) ?? 'English'
  )
}

export function buildConfig(
  promptHint?: string,
  displayMessage?: string,
  dateRange?: { startDate: string; endDate: string },
  featureFlags?: Set<string>,
): AiChatbotConfig {
  const languageName = resolveLanguageName()
  const filterEnabled = featureFlags?.has('advancedSearch') ?? false
  const timeTrackingEnabled = featureFlags?.has('timeTracking') ?? false

  const flags = featureFlags ?? new Set<string>()
  const enabledEntities = Object.keys(getEntityRegistry()).filter((key) =>
    isEntityEnabled(key, flags),
  )

  const pack = getHostToolPack()
  const entityDisplayNames = pack.entityDisplayNames ?? {}
  const displayEntities = enabledEntities.map((e) => entityDisplayNames[e] ?? e)
  // segmentInstructions is present in the pack only when the List entity is
  // enabled on this instance (set conditionally by the connector bootstrap).
  const segmentInstructions = pack.promptFragments.segmentInstructions
  const renderingRules = pack.promptFragments.renderingRules
  const timeTrackingRules = pack.promptFragments.timeTrackingRules

  const config: AiChatbotConfig = {
    systemPrompt: getSystemPrompt({
      language: languageName,
      dateRange,
      browserTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      filterEnabled,
      timeTrackingEnabled,
      enabledEntities: displayEntities,
      renderingRules,
      timeTrackingRules,
      segmentInstructions,
    }),
    pipeline: PIPELINE,
    tools: [
      ...READ_TOOLS,
      ...WRITE_TOOLS,
      ...pack.aggregationTools,
      ...(filterEnabled ? pack.filterTools : []),
    ],
    debug: resolveDebugLevel(),
  }

  if (promptHint) {
    config.initialMessages = [{ role: 'user', content: promptHint }]
    if (displayMessage) {
      config.initialDisplayMessage = displayMessage
    }
  }

  return config
}
